import { Card } from '@/components/common/card'
import { NetworkImage } from '@/components/common/networkImage'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableRowLink } from '@/components/common/table'
import { TokenImage } from '@/components/common/tokenImage'
import { UtilizationTooltip } from '@/components/common/utilizationTooltip'
import { ArrowIcon } from '@/components/icons/arrow'
import { routes } from '@/config/routes'
import { useMixpanel } from '@/context/mixpanel'
import type { BorrowMarketRow } from '@/hooks/useBorrowMarkets'
import type { BorrowMarketsSortState } from '@/types/borrowMarketsSortState'
import type { BorrowMarketsSortableColumn, BorrowMarketsTableColumn } from '@/types/borrowMarketsTableColumn'
import { borrowMarketsTableColumns, isBorrowMarketsSortableColumn } from '@/types/borrowMarketsTableColumn'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { formatDecimalToPercentage, formatUSD } from '@vaultsfyi/common/utils'
import { generatePath } from 'react-router'
import { TableSortButton, type TableSortButtonProps } from '../shared/tableSortButton'
import { BorrowMarketProtocolTooltip } from './borrowMarketProtocolTooltip'
import { BorrowMarketsCollateral } from './borrowMarketsCollateral'
import { BorrowMarketsPagination } from './borrowMarketsPagination'

interface BorrowMarketsTableProps {
  borrowMarkets: BorrowMarketRow[]
  sortState: BorrowMarketsSortState
  onSortByClick: (column: BorrowMarketsSortableColumn) => void
  currentPage: number
  itemsCount: number
  itemsPerPage: number
  className?: string
}

export const BorrowMarketsTable = ({
  borrowMarkets,
  sortState,
  onSortByClick,
  currentPage,
  itemsCount,
  itemsPerPage,
  className,
}: BorrowMarketsTableProps) => {
  const { trackEvent } = useMixpanel()

  return (
    <Card as="section" className={cn('w-full', className)}>
      <ScrollArea type="auto" className="w-full">
        <ScrollBar orientation="horizontal" className="top-0" />
        <Table className="min-w-[1124px] table-fixed">
          <TableHeader>
            <TableRow variant="header">
              {borrowMarketsTableColumns.map((column) => {
                const isSortable = isBorrowMarketsSortableColumn(column)

                return getColumnHeaderElement({
                  name: column,
                  active: isSortable && sortState.sortBy === column,
                  asc: sortState.asc,
                  onClick: isSortable ? () => onSortByClick(column) : undefined,
                })
              })}
              <TableHead className="!pl-1 !pr-3 w-9" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {borrowMarkets.length > 0 ? (
              borrowMarkets.map(({ id, market, assetData, collateralAssets }) => (
                <TableRow className="group" key={id}>
                  <TableCell role="rowheader" className="h-14">
                    <div className="flex items-center gap-2">
                      <TokenImage symbol={assetData.asset.symbol} className="size-8 p-1 group-even:bg-page" />
                      <div>
                        <div className="font-medium text-heading-foreground text-sm">{assetData.asset.symbol}</div>
                        <div className="flex items-center gap-1 text-xs">
                          <NetworkImage network={market.network.name} className="size-4 group-even:bg-page" />
                          <div className="whitespace-nowrap capitalize">{getNetworkName(market.network.name)}</div>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="px-1">
                    <BorrowMarketProtocolTooltip protocol={market.protocol} className="size-8 p-1 group-even:bg-page" />
                  </TableCell>
                  <TableCell>
                    <span className="block max-w-full truncate">{market.name}</span>
                  </TableCell>
                  <TableCell>
                    <BorrowMarketsCollateral assets={collateralAssets} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {assetData.totalSupplied?.usd ? formatUSD(assetData.totalSupplied.usd) : '--'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <div className="ml-auto grid w-fit grid-cols-[auto,4.5rem] items-center justify-end gap-1">
                      {assetData.totalSupplied?.native && assetData.availableLiquidity?.native && (
                        <UtilizationTooltip
                          tabIndex={-1}
                          totalSupplied={assetData.totalSupplied.native}
                          availableLiquidity={assetData.availableLiquidity.native}
                          utilizationRate={assetData.utilizationRate}
                        />
                      )}
                      <span className="[&:only-child]:col-span-full">
                        {assetData.availableToBorrow?.usd ? formatUSD(assetData.availableToBorrow.usd) : '--'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {assetData.borrowRate !== undefined ? formatDecimalToPercentage(assetData.borrowRate) : '--'}
                  </TableCell>
                  <TableCell className="!pl-1 !pr-3">
                    <TableRowLink
                      to={generatePath(routes.borrowMarket, {
                        network: market.network.name,
                        marketId: market.marketId,
                        assetId: assetData.marketAssetId,
                      })}
                      title={`${assetData.asset.symbol} borrow market`}
                      prefetch="intent"
                      onClick={() =>
                        trackEvent('Borrow markets table row clicked', {
                          asset: assetData.asset.symbol,
                          network: market.network.name,
                          marketId: market.marketId,
                        })
                      }
                      className="focus-visible:before:rounded-xl focus-visible:before:ring focus-visible:before:ring-inset"
                    >
                      <ArrowIcon />
                    </TableRowLink>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={borrowMarketsTableColumns.length + 1} className="text-center">
                  <div className="flex w-full items-center justify-center py-6">No borrow markets found</div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      {itemsCount > 0 && (
        <BorrowMarketsPagination
          currentPage={currentPage}
          itemsCount={itemsCount}
          itemsPerPage={itemsPerPage}
          className="border-t"
        />
      )}
    </Card>
  )
}

interface ColumnHeaderProps extends TableSortButtonProps {
  name: BorrowMarketsTableColumn
}

function getColumnHeaderElement({ name, ...props }: ColumnHeaderProps) {
  switch (name) {
    case 'protocol':
      return (
        <TableHead key={name} className="w-[88px] px-1">
          <TableSortButton className="capitalize" {...props}>
            {name}
          </TableSortButton>
        </TableHead>
      )
    case 'collateral':
      return <TableHead key={name}>Collateral</TableHead>
    case 'marketSize':
      return (
        <TableHead key={name} className="text-right">
          <TableSortButton className="ml-auto" {...props}>
            Market size
          </TableSortButton>
        </TableHead>
      )
    case 'availableToBorrow':
      return (
        <TableHead key={name} className="text-right">
          <TableSortButton className="ml-auto" {...props}>
            Available to borrow
          </TableSortButton>
        </TableHead>
      )
    case 'borrowRate':
      return (
        <TableHead key={name} className="text-right">
          <TableSortButton className="ml-auto" {...props}>
            Borrow rate
          </TableSortButton>
        </TableHead>
      )
    case 'name':
      return (
        <TableHead key={name}>
          <TableSortButton className="capitalize" {...props}>
            {name}
          </TableSortButton>
        </TableHead>
      )
    case 'token':
    default:
      return (
        <TableHead key={name} className="w-[168px]">
          <TableSortButton className="capitalize" {...props}>
            {name}
          </TableSortButton>
        </TableHead>
      )
  }
}
